import { useState } from 'react';
import {
  Box,
  CircularProgress,
  Collapse,
  FormControl,
  Link,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent, SxProps, Theme } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorIcon from '@mui/icons-material/Error';

import { RatingBadgeChip } from '@/components/common';
import { RichTextEditor } from '@/components/common/RichTextEditor/RichTextEditor';
import { RichTextReadOnly } from '@/components/common/RichTextEditor/RichTextReadOnly';
import { useUpdateSkillAreaFeedback, useSkillAreaDetails } from '@/queries/useAppraisalDetail';
import type { SkillAreaFeedback, SkillAreaDetail } from '@/api/types/appraisal.types';
import type { ReadOnlyVariant } from '@/auth/sectionReadOnlyStyle';

const RATING_OPTIONS = [
  { value: 'BT', label: 'BT' },
  { value: 'OT', label: 'OT' },
  { value: 'AT', label: 'AT' },
  { value: 'O', label: 'O' },
];

const skillAreaWrapperStyles: SxProps<Theme> = {
  bgcolor: 'common.white',
  border: 1,
  borderColor: 'grey.300',
  mt: 1,
};

const tableHeaderCellSx: SxProps<Theme> = {
  fontWeight: 'bold',
  bgcolor: 'custom.deepTeal',
  color: 'common.white',
};

const tableBodyCellSx: SxProps<Theme> = {
  verticalAlign: 'top',
  whiteSpace: 'pre-line',
};

interface SkillAreaCardProps {
  area: SkillAreaFeedback;
  index: number;
  appraisalId: number;
  canEdit: boolean;
  hasError: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  rowVersion?: string;
  readOnlyVariant?: ReadOnlyVariant;
}

export function SkillAreaCard({
  area,
  index,
  appraisalId,
  canEdit,
  hasError,
  isExpanded,
  onToggleExpand,
  rowVersion,
  readOnlyVariant = 'boxed',
}: SkillAreaCardProps) {
  const [localRating, setLocalRating] = useState<string | null>(area.rating);
  const [localText, setLocalText] = useState<string>(area.combinedText ?? '');
  const [prevRating, setPrevRating] = useState(area.rating);
  const [prevText, setPrevText] = useState(area.combinedText);

  if (prevRating !== area.rating) {
    setPrevRating(area.rating);
    setLocalRating(area.rating);
  }
  if (prevText !== area.combinedText) {
    setPrevText(area.combinedText);
    setLocalText(area.combinedText ?? '');
  }

  const { mutate: updateFeedback } = useUpdateSkillAreaFeedback();
  const { data: skillDetails, isLoading: isDetailsLoading } = useSkillAreaDetails(
    String(appraisalId),
    area.skillAreaId,
    isExpanded
  );

  const cardHasError = hasError && !localRating;
  const areaLabel = `${index + 1}. ${area.skillAreaName}`;

  const handleRatingChange = (event: SelectChangeEvent<string>) => {
    const newRating = event.target.value;
    setLocalRating(newRating);
    updateFeedback({
      appraisalId,
      skillAreaId: area.skillAreaId,
      data: { rating: newRating, combinedText: localText || null },
      rowVersion,
    });
  };

  const handleTextBlur = () => {
    updateFeedback({
      appraisalId,
      skillAreaId: area.skillAreaId,
      data: { rating: localRating, combinedText: localText || null },
      rowVersion,
    });
  };

  const errorWrapperStyles = cardHasError
    ? { borderColor: 'error.main', bgcolor: '#fff0f0' }
    : {};

  return (
    <Box sx={{ ...skillAreaWrapperStyles, ...errorWrapperStyles }}>
      <Box sx={{ py: 1.5, px: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={1}
        >
          <Typography variant="body1" fontWeight="medium">
            {areaLabel}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Rating
            </Typography>
            {canEdit ? (
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                  value={localRating ?? ''}
                  onChange={handleRatingChange}
                  displayEmpty
                  aria-label={`Rating for ${area.skillAreaName}`}
                  aria-invalid={cardHasError ? true : undefined}
                  renderValue={(selected) => selected || 'Select'}
                >
                  {RATING_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <RatingBadgeChip code={area.updatedRating ?? area.rating} />
            )}
            {cardHasError && <ErrorIcon color="error" fontSize="small" />}
            <ShowStandardsLink
              isExpanded={isExpanded}
              isLoading={isDetailsLoading}
              onToggle={onToggleExpand}
            />
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ px: 2, pb: 2 }}>
        {canEdit ? (
          <RichTextEditor
            content={localText}
            onChange={setLocalText}
            onBlur={handleTextBlur}
            placeholder="Type here..."
          />
        ) : (
          <RichTextReadOnly
            content={area.combinedText ?? ''}
            emptyMessage="No strengths or development needs recorded."
            variant={readOnlyVariant}
          />
        )}
      </Box>

      <Collapse in={isExpanded && !isDetailsLoading}>
        <SkillDescriptionsTable skillDetails={skillDetails} />
      </Collapse>
    </Box>
  );
}

// =============================================================================
// Show Standards Link
// =============================================================================

interface ShowStandardsLinkProps {
  isExpanded: boolean;
  isLoading: boolean;
  onToggle: () => void;
}

function ShowStandardsLink({ isExpanded, isLoading, onToggle }: ShowStandardsLinkProps) {
  if (isLoading) {
    return <CircularProgress size={14} sx={{ ml: 2 }} />;
  }

  return (
    <Link
      component="button"
      variant="body2"
      onClick={onToggle}
      aria-expanded={isExpanded}
      sx={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 0.5 }}
    >
      <ExpandMoreIcon
        sx={{
          fontSize: 18,
          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s',
        }}
      />
      {isExpanded ? 'Hide standards' : 'Show standards'}
    </Link>
  );
}

// =============================================================================
// Skill Descriptions Table
// =============================================================================

interface SkillDescriptionsTableProps {
  skillDetails?: SkillAreaDetail[];
}

function SkillDescriptionsTable({ skillDetails }: SkillDescriptionsTableProps) {
  if (!skillDetails || skillDetails.length === 0) {
    return (
      <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Typography variant="body2" color="text.secondary">
          No standards defined for this area.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2, pb: 2, overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...tableHeaderCellSx, width: '15%' }}>SKILL</TableCell>
            <TableCell sx={{ ...tableHeaderCellSx, width: '21.25%' }}>BT</TableCell>
            <TableCell sx={{ ...tableHeaderCellSx, width: '21.25%' }}>OT</TableCell>
            <TableCell sx={{ ...tableHeaderCellSx, width: '21.25%' }}>AT</TableCell>
            <TableCell sx={{ ...tableHeaderCellSx, width: '21.25%' }}>O</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {skillDetails.map((skill) => (
            <TableRow key={skill.skillId}>
              <TableCell sx={{ ...tableBodyCellSx, fontWeight: 'medium' }}>
                {skill.skillName}
              </TableCell>
              <TableCell sx={tableBodyCellSx}>
                <Typography variant="body2">{skill.btDescription || '-'}</Typography>
              </TableCell>
              <TableCell sx={tableBodyCellSx}>
                <Typography variant="body2">{skill.otDescription || '-'}</Typography>
              </TableCell>
              <TableCell sx={tableBodyCellSx}>
                <Typography variant="body2">{skill.atDescription || '-'}</Typography>
              </TableCell>
              <TableCell sx={tableBodyCellSx}>
                <Typography variant="body2">{skill.oDescription || '-'}</Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

export default SkillAreaCard;
